<template>
  <div class="pdf-list">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>我的PDF文件</span>
          <el-button type="primary" @click="$router.push('/generate')">
            <el-icon><DocumentAdd /></el-icon>
            生成新PDF
          </el-button>
        </div>
      </template>
      
      <!-- 搜索和筛选 -->
      <div class="filter-bar">
        <el-row :gutter="20">
          <el-col :span="8">
            <el-input
              v-model="searchKeyword"
              placeholder="搜索文件名"
              prefix-icon="Search"
              clearable
              @input="handleSearch"
            />
          </el-col>
          <el-col :span="6">
            <el-select
              v-model="filterAlgorithm"
              placeholder="筛选算法类型"
              clearable
              @change="handleFilter"
            >
              <el-option label="全部" value="" />
              <el-option
                v-for="type in algorithmTypes"
                :key="type"
                :label="getAlgorithmName(type)"
                :value="type"
              />
            </el-select>
          </el-col>
          <el-col :span="6">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              @change="handleFilter"
            />
          </el-col>
          <el-col :span="4">
            <el-button @click="refreshList">
              <el-icon><Refresh /></el-icon>
              刷新
            </el-button>
          </el-col>
        </el-row>
      </div>
      
      <!-- PDF列表 -->
      <div class="pdf-table">
        <el-table
          :data="filteredPdfs"
          style="width: 100%"
          v-loading="loading"
          empty-text="暂无PDF文件"
        >
          <el-table-column prop="filename" label="文件名" min-width="200">
            <template #default="scope">
              <div class="filename-cell">
                <el-icon><Document /></el-icon>
                <span>{{ scope.row.filename }}</span>
              </div>
            </template>
          </el-table-column>
          
          <el-table-column prop="algorithm_type" label="算法类型" width="150">
            <template #default="scope">
              <el-tag type="info" size="small">
                {{ getAlgorithmName(scope.row.algorithm_type) }}
              </el-tag>
            </template>
          </el-table-column>
          
          <el-table-column prop="created_at" label="创建时间" width="180">
            <template #default="scope">
              {{ formatDate(scope.row.created_at) }}
            </template>
          </el-table-column>
          
          <el-table-column label="文件大小" width="100">
            <template #default="scope">
              <span class="file-size">{{ getFileSize(scope.row.filename) }}</span>
            </template>
          </el-table-column>
          
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button
                type="primary"
                size="small"
                @click="downloadPdf(scope.row.filename)"
              >
                <el-icon><Download /></el-icon>
                下载
              </el-button>
              <el-button
                type="success"
                size="small"
                @click="previewPdf(scope.row.filename)"
              >
                <el-icon><View /></el-icon>
                预览
              </el-button>
              <el-button
                type="danger"
                size="small"
                @click="deletePdf(scope.row.id, scope.row.filename)"
              >
                <el-icon><Delete /></el-icon>
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      
      <!-- 分页 -->
      <div class="pagination" v-if="filteredPdfs.length > 0">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="filteredPdfs.length"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
    
    <!-- PDF预览对话框 -->
    <el-dialog
      v-model="previewVisible"
      title="PDF预览"
      width="80%"
      :before-close="closePreview"
    >
      <div class="pdf-preview">
        <iframe
          v-if="previewUrl"
          :src="previewUrl"
          width="100%"
          height="600px"
          frameborder="0"
        ></iframe>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'

export default {
  name: 'PdfList',
  setup() {
    const loading = ref(false)
    const pdfs = ref([])
    const searchKeyword = ref('')
    const filterAlgorithm = ref('')
    const dateRange = ref([])
    const currentPage = ref(1)
    const pageSize = ref(20)
    const previewVisible = ref(false)
    const previewUrl = ref('')
    
    const algorithmTypes = computed(() => {
      const types = [...new Set(pdfs.value.map(pdf => pdf.algorithm_type))]
      return types.filter(type => type)
    })
    
    const filteredPdfs = computed(() => {
      let result = pdfs.value
      
      // 搜索过滤
      if (searchKeyword.value) {
        result = result.filter(pdf => 
          pdf.filename.toLowerCase().includes(searchKeyword.value.toLowerCase())
        )
      }
      
      // 算法类型过滤
      if (filterAlgorithm.value) {
        result = result.filter(pdf => pdf.algorithm_type === filterAlgorithm.value)
      }
      
      // 日期范围过滤
      if (dateRange.value && dateRange.value.length === 2) {
        const [startDate, endDate] = dateRange.value
        result = result.filter(pdf => {
          const pdfDate = pdf.created_at.split('T')[0]
          return pdfDate >= startDate && pdfDate <= endDate
        })
      }
      
      return result
    })
    
    const loadPdfs = async () => {
      loading.value = true
      try {
        const response = await axios.get('/api/pdfs')
        pdfs.value = response.data.pdfs
      } catch (error) {
        ElMessage.error('加载PDF列表失败')
      } finally {
        loading.value = false
      }
    }
    
    const handleSearch = () => {
      currentPage.value = 1
    }
    
    const handleFilter = () => {
      currentPage.value = 1
    }
    
    const refreshList = () => {
      loadPdfs()
    }
    
    const formatDate = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN')
    }
    
    const getAlgorithmName = (algorithmType) => {
      const names = {
        'algorithm_1': '职达hero56+40',
        'algorithm_2': '职达hero48+25',
        'algorithm_3': '怪兽求职56+40',
        'algorithm_4': '怪兽求职56+40',
        'algorithm_5': '未定义算法A',
        'algorithm_6': '未定义算法B',
        'algorithm_7': '未定义算法C',
        'algorithm_8': '未定义算法D'
      }
      return names[algorithmType] || algorithmType
    }
    
    const getFileSize = (filename) => {
      // 这里可以实现获取文件大小的逻辑
      // 目前返回占位符
      return '-- KB'
    }
    
    const downloadPdf = async (filename) => {
      try {
        const response = await axios.get(`/api/pdfs/${filename}/download`, {
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        
        ElMessage.success('下载成功')
      } catch (error) {
        ElMessage.error('下载失败')
      }
    }
    
    const previewPdf = async (filename) => {
      try {
        const response = await axios.get(`/api/pdfs/${filename}/download`, {
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
        previewUrl.value = url
        previewVisible.value = true
      } catch (error) {
        ElMessage.error('预览失败')
      }
    }
    
    const closePreview = () => {
      if (previewUrl.value) {
        window.URL.revokeObjectURL(previewUrl.value)
        previewUrl.value = ''
      }
      previewVisible.value = false
    }
    
    const deletePdf = async (pdfId, filename) => {
      try {
        await ElMessageBox.confirm(
          `确定要删除文件 "${filename}" 吗？此操作不可恢复。`,
          '确认删除',
          {
            confirmButtonText: '确定删除',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        await axios.delete(`/api/pdfs/${pdfId}`)
        ElMessage.success('删除成功')
        
        // 重新加载列表
        await loadPdfs()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }
    
    const handleSizeChange = (newSize) => {
      pageSize.value = newSize
      currentPage.value = 1
    }
    
    const handleCurrentChange = (newPage) => {
      currentPage.value = newPage
    }
    
    onMounted(() => {
      loadPdfs()
    })
    
    return {
      loading,
      pdfs,
      searchKeyword,
      filterAlgorithm,
      dateRange,
      currentPage,
      pageSize,
      previewVisible,
      previewUrl,
      algorithmTypes,
      filteredPdfs,
      handleSearch,
      handleFilter,
      refreshList,
      formatDate,
      getAlgorithmName,
      getFileSize,
      downloadPdf,
      previewPdf,
      closePreview,
      deletePdf,
      handleSizeChange,
      handleCurrentChange
    }
  }
}
</script>

<style scoped>
.pdf-list {
  padding: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-bar {
  margin-bottom: 20px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.pdf-table {
  margin-bottom: 20px;
}

.filename-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.file-size {
  color: #999;
  font-size: 12px;
}

.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.pdf-preview {
  text-align: center;
}

.el-table .el-button {
  margin-right: 5px;
}

.el-table .el-button:last-child {
  margin-right: 0;
}
</style>